/**
 * La cola de la línea sin red.
 *
 * Una línea de empaque no siempre tiene conectividad, y parar de envasar
 * porque se cayó el wifi no es una opción. El endpoint está pensado para eso:
 * el cuerpo lleva `client_batch_id` (clave de idempotencia que elige el
 * cliente) y `client_sent_at` (el reloj de la línea, que se guarda como marca
 * de activación aunque los conflictos se resuelvan por orden de llegada al
 * servidor).
 *
 * Acá vive la parte del cliente: acumular tandas mientras no hay red y
 * reenviarlas después **con la misma clave**, que es lo que hace que
 * reconectar no active dos veces.
 *
 * La cola vive en memoria y no en `localStorage` a propósito: son códigos
 * impresos de producto farmacéutico y no tienen por qué quedar en el disco
 * del navegador después de cerrar la pestaña. Una línea de verdad los
 * persistiría en su propio buffer local, del lado de la planta.
 */

/** Una tanda leída en la línea, lista para enviar o esperando red. */
export interface PendingRun {
  /** Clave de idempotencia. UNIQUE `(tenant_id, client_batch_id)` en la base. */
  clientBatchId: string;
  batchId: number;
  lineId: string | null;
  codes: string[];
  /** Reloj de la LÍNEA al cerrar la tanda, no el del servidor. ISO 8601. */
  clientSentAt: string;
}

/** Cuerpo de `POST /api/v1/activations/bulk`. */
export interface ActivationBulkBody {
  client_batch_id: string;
  batch_id: number;
  codes: string[];
  line_id: string | null;
  client_sent_at: string;
}

export function toBulkBody(run: PendingRun): ActivationBulkBody {
  return {
    client_batch_id: run.clientBatchId,
    batch_id: run.batchId,
    codes: run.codes,
    line_id: run.lineId,
    client_sent_at: run.clientSentAt,
  };
}

export interface EnqueueResult {
  queue: PendingRun[];
  /** `true` si había otra tanda con la misma clave y ésta la reemplazó. */
  replaced: boolean;
}

/**
 * Encola una tanda. Si ya había una con la misma clave, la reemplaza y lo
 * dice: dos tandas distintas bajo la misma clave no pueden salir las dos —el
 * servidor rechazaría la segunda con `activation_batch_mismatch`— y mandar
 * una y descartar la otra en silencio perdería etiquetas leídas.
 */
export function enqueue(queue: readonly PendingRun[], run: PendingRun): EnqueueResult {
  const index = queue.findIndex((item) => item.clientBatchId === run.clientBatchId);
  if (index === -1) return { queue: [...queue, run], replaced: false };
  const next = [...queue];
  next[index] = run;
  return { queue: next, replaced: true };
}

/** Saca una tanda de la cola, típicamente porque el servidor ya la aceptó. */
export function dequeue(queue: readonly PendingRun[], clientBatchId: string): PendingRun[] {
  return queue.filter((item) => item.clientBatchId !== clientBatchId);
}

/** Cuántas etiquetas están esperando red. */
export function queuedCodes(queue: readonly PendingRun[]): number {
  return queue.reduce((total, run) => total + run.codes.length, 0);
}
